import {
    BranchListItems,
    MapBranch,
    MapBranches,
    MaterialBranch,
    Reminder,
    Task,
    TaskDevices,
    UserTasks,
    Trouble,
    UserTasksPage,
} from '../models/dashboard-models';
import { transformNoPagerResponse, transformTableResponse } from './data-hooks';
import { Buffer } from 'buffer';
import interceptor from '../restServiceInterceptor';
import { useAsyncResource } from 'use-async-resource';
import dateFormat from 'dateformat';
import { BranchDevice } from '../models/warehouse-models';

const emptyMapBranches: MapBranches = { items: [], pager: {} as MapBranches['pager'] };
const emptyDashboardBranches: BranchListItems = { items: [], pager: {} as BranchListItems['pager'] };
export const DASHBOARD_BRANCHES_PAGE_SIZE = 20;

export const fetchRemindersAll = (): Promise<Reminder[]> => {
    return interceptor.get('/work-service/reminders/all', {
        transformResponse: transformNoPagerResponse,
    });
};

export const disableRemindersAll = (reminderId: number) => {
    return interceptor.post(`/work-service/reminders/all/disable?reminderId=${reminderId}`);
};

export const fetchRemindersYours = (): Promise<Reminder[]> => {
    return interceptor.get('/work-service/reminders/yours', {
        transformResponse: transformNoPagerResponse,
    });
};
export const fetchTasksAll = (
    onlyUnfinished?: boolean,
    assigneeId?: string,
    before?: string | undefined,
    after?: string | undefined,
    search?: string | undefined,
    isDistributor?: boolean,
    page?: number,
    itemsPerPage?: number,
    sortDirection: string = 'ASC',
): Promise<UserTasksPage> => {
    const url = '/work-service/tasks/tools/getAll';
    return interceptor.get(url, {
        params: { onlyUnfinished, assigneeId, toDate: before, fromDate: after, branchName: search ?? null, page, itemsPerPage, sortDirection },
        transformResponse: transformNoPagerResponse,
    });
};

const TASKS_FETCH_ALL_PAGE_SIZE = 200;

const flattenUserTasks = (userTasks: UserTasks[] = []): Task[] => {
    return userTasks.flatMap(({ notFinishedTasks, finishedTasks }) => notFinishedTasks.concat(finishedTasks));
};

export const fetchAllTasksForFilters = async (
    onlyUnfinished?: boolean,
    assigneeId?: string,
    before?: string | undefined,
    after?: string | undefined,
    search?: string | undefined,
    isDistributor?: boolean,
    sortDirection: string = 'ASC',
): Promise<Task[]> => {
    const firstPage = await fetchTasksAll(
        onlyUnfinished,
        assigneeId,
        before,
        after,
        search,
        isDistributor,
        1,
        TASKS_FETCH_ALL_PAGE_SIZE,
        sortDirection
    );

    const allTasks = flattenUserTasks(firstPage.items ?? []);
    const totalPages = firstPage.pager?.pagesCount ?? 1;

    if (totalPages <= 1) {
        return allTasks;
    }

    for (let page = 2; page <= totalPages; page += 1) {
        const currentPage = await fetchTasksAll(
            onlyUnfinished,
            assigneeId,
            before,
            after,
            search,
            isDistributor,
            page,
            TASKS_FETCH_ALL_PAGE_SIZE,
            sortDirection
        );
        allTasks.push(...flattenUserTasks(currentPage.items ?? []));
    }

    return allTasks;
};

export const fetchAssigneeIdsWithTasks = (
    onlyUnfinished?: boolean,
    before?: string | undefined,
    after?: string | undefined,
    search?: string | undefined,
): Promise<number[]> => {
    return interceptor.get('/work-service/tasks/tools/assigneesWithTasks', {
        params: { onlyUnfinished, toDate: before, fromDate: after, branchName: search ?? null },
        transformResponse: transformNoPagerResponse,
    });
};

export const setTaskOrder = (body: { taskId: number; priority: number }[]) => {
    return interceptor.post('/work-service/tasks/tools/sort', body);
};
export const deleteTask = (task?: Task | undefined) => {
    return interceptor.post('/work-service/tasks/tools/remove', {}, { params: { taskId: task?.id } });
};

export const deleteTasks = (taskIds: number[]) => {
    return interceptor.post('/work-service/tasks/tools/remove/selected', {}, {
        params: { taskIds },
        paramsSerializer: (params) => {
            return Object.keys(params)
                .map((key) => {
                    const value = params[key];
                    if (Array.isArray(value)) {
                        return value
                            .map((val) => `${encodeURIComponent(key)}=${encodeURIComponent(val)}`)
                            .join('&');
                    }
                    return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
                })
                .join('&');
        },
    });
};

export const updateTaskNeededItems = (task: Task, devices?: BranchDevice[], materials?: MaterialBranch[]) => {
    const updateTaskVO = { devices: devices, materials: materials };
    return interceptor.post('/work-service/tasks/update', updateTaskVO, { params: { taskId: task?.id } });
};

export const completeTaskAsCoordinator = (
    task?: Task | undefined,
    taskComplete?: TaskDevices,
    isDistributor?: boolean,
) => {
    const url = isDistributor ? '/work-service/tasks/complete' : '/work-service/tasks/tools/complete';
    return interceptor.post(
        url,
        {
            ...taskComplete,
            isTestTask:task?.isTest,
            isTestEnd: false,
            branchId: task?.branchId,
        },
        { params: { taskId: task?.id } },
    );
};

export const fetchTasksYours = (onlyUnfinished?: boolean, date?: Date): Promise<Task[]> => {
    if (!onlyUnfinished) onlyUnfinished = true;
    return interceptor.get('/work-service/tasks', {
        params: {
            onlyUnfinished: onlyUnfinished,
            date: date?.toLocaleDateString(),
        },
        transformResponse: transformNoPagerResponse,
    });
};

export const fetchTroublesAll = (): Promise<Trouble[]> => {
    return interceptor.get('/work-service/trouble-tickets/group', {
        transformResponse: transformNoPagerResponse,
    });
};

export const fetchMapBranches = (props?: { filter?: {} }): Promise<MapBranches> => {
    let search = props?.filter ? { ...props.filter } : null;
    return (interceptor.get('/client-service/clients/branches?view=MAPVIEW', {
        params: {
            ...search,
        },
        transformResponse: transformTableResponse,
    }) as Promise<MapBranches>).catch((error) => {
        console.error('Failed to load map branches:', error);
        return emptyMapBranches;
    });
};

export const fetchDashboardBranches = (props?: { filter?: {} }): Promise<BranchListItems> => {
    let search = props?.filter ? { ...props.filter } : null;
    return (interceptor.get('/client-service/clients/branches?view=DASHBOARD', {
        params: {
            ...search,
        },
        transformResponse: transformTableResponse,
    }) as Promise<BranchListItems>).catch((error) => {
        console.error('Failed to load dashboard branches:', error);
        return emptyDashboardBranches;
    });
};

const TRANSFER_DASHBOARD_BRANCHES_PAGE_SIZE = 100;

export const fetchAllDashboardBranchesByDistributor = async (
    distributorId: number
): Promise<BranchListItems['items']> => {
    const firstPage = await fetchDashboardBranches({
        filter: {
            assignedDistributor: distributorId,
            page: 1,
            itemsPerPage: TRANSFER_DASHBOARD_BRANCHES_PAGE_SIZE,
        },
    });

    const totalPages = firstPage.pager?.pagesCount ?? 1;

    if (totalPages <= 1) {
        return firstPage.items ?? [];
    }

    const remainingPages = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, index) =>
            fetchDashboardBranches({
                filter: {
                    assignedDistributor: distributorId,
                    page: index + 2,
                    itemsPerPage: TRANSFER_DASHBOARD_BRANCHES_PAGE_SIZE,
                },
            })
        )
    );

    return [
        ...(firstPage.items ?? []),
        ...remainingPages.flatMap((pageResult) => pageResult.items ?? []),
    ];
};

export const fetchMapBranchById = (id: number): Promise<MapBranch> => {
    return interceptor.get(`/client-service/clients/branches/byView/${id}?view=MAPVIEW`, {
        transformResponse: transformNoPagerResponse,
    });
};

export const fetchDashboardBranchById = (id: number): Promise<MapBranch> => {
    return interceptor.get(`/client-service/clients/branches/byView/${id}?view=DASHBOARD`, {
        transformResponse: transformNoPagerResponse,
    });
};

export const fetchBranchImage = async (id: number): Promise<string> => {
    const response: ArrayBuffer = await interceptor.get(`/client-service/clients/branches/${id}/image`, {
        responseType: 'arraybuffer',
    });
    const raw = Buffer.from(response).toString('base64');

    const base64Image = raw ? `data:image/gif;base64,${raw}` : '';

    return base64Image;
};

export const useAddReminder = (fields: Reminder): Promise<Reminder> => {
    return interceptor.post('/work-service/reminders/yours/add', {
        ...fields,
        endTime: dateFormat(fields.endTime, 'isoUtcDateTime'),
        transformResponse: transformNoPagerResponse,
    });
};


export const disableReminderYours = (reminderId: number) => {
    return interceptor.post('/work-service/reminders/yours/disable', null, {
        params: { reminderId },
    });
};

export const useAddTrouble = (fields: Trouble) => {
    if (fields.branchId) {
        return interceptor.post('/work-service/trouble-tickets/add', {
            ...fields,
        });
    } else {
        console.log('error');
    }
};

export const useResolveTrouble = (id: number) => {
    return interceptor.post(`/work-service/trouble-tickets/group/markDone?ticketId=${id}`);
};

export const useGetMapBranches = (filter?: {}) => {
    return useAsyncResource(fetchMapBranches, { filter });
};

export const useGetDashboardBranches = (filter?: {}) => {
    return useAsyncResource(fetchDashboardBranches, { filter });
};

export const useGetBranchImage = (id: number) => {
    return useAsyncResource(fetchBranchImage, id);
};

export const useGetRemindersAll = () => {
    return useAsyncResource(fetchRemindersAll, []);
};

export const useGetRemindersYours = () => {
    return useAsyncResource(fetchRemindersYours, []);
};

export const useGetAllTasks = (
    onlyUnfinished?: boolean,
    userId?: string,
    before?: string | undefined,
    after?: string | undefined,
    search?: string | undefined,
    isDistributor?: boolean,
    page?: number,
    itemsPerPage?: number,
    sortDirection?: string,
) => {
    return useAsyncResource(fetchTasksAll, onlyUnfinished, userId, before, after, search, isDistributor, page, itemsPerPage, sortDirection ?? 'ASC');
};

export const useGetYourTasks = () => {
    return useAsyncResource(fetchTasksYours);
};

export const useGetTroublesAll = () => {
    return useAsyncResource(fetchTroublesAll, []);
};
